/**
 * # Revision model — five browser-minted monotonic revision counters + epoch
 * (AGENT-BRIDGE-BROWSER slice ABB-S1)
 *
 * The AGENT-BRIDGE companion forwards a `command.run` batch carrying the agent's
 * `base{Snapshot,Geometry,Table,Context}Revision`; the BROWSER adjudicates the
 * precondition by EXACT-equality of those bases against the LIVE counters this
 * model owns. The counters are the typed CAD-core source of truth for "what kind
 * of change has the live document undergone since the agent last read it" — NOT
 * an ad-hoc React/store scalar (CLAUDE.md ПРИОРИТЕТ #1: new CAD semantics live in
 * the typed package core, then project into the store/UI).
 *
 * ## The five counters (+ documentEpoch)
 *
 *  - `snapshotRevision`  — bumps on EVERY committed mutation (the COMPANION INV-9
 *      coarse authority for `baseSnapshotRevision`). One bump per committed turn.
 *  - `geometryRevision`  — entity-STATE: geometry add/erase/move/grip/transform OR
 *      a CHPROP / MATCHPROP / Properties property/metadata edit on an entity.
 *  - `tableRevision`     — layer / block / layout / plot / units / style / xref
 *      (the named-object + header tables), i.e. document structure that is not an
 *      entity's own geometry/property state.
 *  - `contextRevision`   — active-space switch + BEDIT/REFEDIT space switch (and,
 *      in a later slice, drafting sysvars). The "what space am I editing" axis.
 *  - `selectionRevision` — selection-set membership.
 *  - `documentEpoch`     — monotonic per LOADED document. Re-minted (and all five
 *      counters reset) only on a true document SWAP (OPEN a different file in the
 *      same tab), never on an INSERT/import re-stamp of the same document id.
 *
 * ## DUAL-BUMP (INV-9)
 *
 * Every committed mutation bumps `snapshotRevision` ONCE plus EACH finer sibling
 * whose bump-class appears in the commit. A multi-class batch (e.g. geometry +
 * table + context in one undo-group) bumps `snapshotRevision` once and EACH of
 * the three finer counters once. An empty / aborted commit bumps NOTHING.
 *
 * ## dep-graph (ПРИОРИТЕТ #2)
 *
 * This module is in `drawing-model`, which `contracts` DEPENDS ON
 * (`contracts → drawing-model`). It therefore MUST NOT import `contracts`
 * (that would make a `contracts ↔ drawing-model` cycle — a hard `deps:graph`
 * blocker). The bump-class classifier consequently parses the command-type
 * STRING itself (mirroring the domain-parse in `contracts/auditTaxonomy.ts`
 * `categoryForCommandType`) rather than importing `AuditCategory`. Exhaustive
 * coverage over the real command unions is guaranteed by `revisionModel.test.ts`,
 * which CAN import `contracts` and enumerates every `editorCommandSchema` +
 * `dynamicBlockCommandSchema` literal (the same pattern as `auditTaxonomy.test.ts`)
 * — a new command kind that maps to `none` by default fails that test until it is
 * deliberately classified here.
 */

import { isDynamicBlockAuthoringRest } from "./blockAuthoringKinds";

/**
 * The five revision counters + the document epoch. Plain numbers (no zod schema):
 * these are LIVE, in-memory, non-persisted store fields (siblings of the
 * render-cache `entityMetadataIndexVersion` / `renderTopologyVersion`), not part
 * of the serialised shell/geometry snapshot. They reset on document swap and do
 * NOT survive a page reload (a reload is a fresh session + fresh epoch).
 */
export interface RevisionCounters {
  readonly snapshotRevision: number;
  readonly geometryRevision: number;
  readonly tableRevision: number;
  readonly contextRevision: number;
  readonly selectionRevision: number;
  readonly documentEpoch: number;
}

/**
 * The finer bump classes a committed mutation can touch. `snapshotRevision` is
 * NOT a member — it is the coarse counter that bumps whenever ANY finer class is
 * present, handled by {@link bumpForClasses}.
 */
export type BumpClass = "geometry" | "table" | "context" | "selection";

/** All four finer classes, for matrix tests + iteration. */
export const BUMP_CLASSES: readonly BumpClass[] = [
  "geometry",
  "table",
  "context",
  "selection",
] as const;

/**
 * An immutable set of finer bump classes for one (or an accumulated batch of)
 * committed mutation(s). Represented as a frozen record so it survives a
 * structural-clone / zustand snapshot intact and has no `Set` identity hazards.
 */
export type BumpClassSet = Readonly<Record<BumpClass, boolean>>;

/** The empty bump set — a committed nothing (empty batch, aborted turn, view-op). */
export const EMPTY_BUMP_CLASSES: BumpClassSet = Object.freeze({
  geometry: false,
  table: false,
  context: false,
  selection: false,
});

/** Whether any finer class is present (⇔ snapshotRevision should bump). */
export function hasAnyBumpClass(classes: BumpClassSet): boolean {
  return (
    classes.geometry ||
    classes.table ||
    classes.context ||
    classes.selection
  );
}

/** Union (accumulate) two bump-class sets — used to fold a batch's commands. */
export function unionBumpClasses(
  a: BumpClassSet,
  b: BumpClassSet,
): BumpClassSet {
  return Object.freeze({
    geometry: a.geometry || b.geometry,
    table: a.table || b.table,
    context: a.context || b.context,
    selection: a.selection || b.selection,
  });
}

/** Build a single-class set (the common case for one committed command). */
function singleBumpClass(bumpClass: BumpClass): BumpClassSet {
  return Object.freeze({ ...EMPTY_BUMP_CLASSES, [bumpClass]: true });
}

// ── State-DIFF bump classifier (the convergent, oracle-free mechanism) ───────
//
// The command-kind classifier above maps a command TYPE to a bump set. It is
// the right tool for folding a synthetic `history/replay-*` batch (where the
// applied class is known from the recorded members, not from a live state
// diff). But for the LIVE commit chokepoint it is fragile: it misses paths that
// resolve a wrapper command into a different mutation (the CLI `commandline/
// submit` → geometry), it mis-classes commands whose effect depends on RUNTIME
// state (CANNOSCALE-sync materialising entities, remove-ACTIVE-space changing
// context, block-edit `createIfMissing` writing the block table), and it bumps
// phantom revisions for NO-OP commits (re-selecting the same set, re-activating
// the current space). All four failure classes vanish if the bump is grounded
// in the ACTUAL state change rather than the command kind.
//
// `diffBumpClasses` consumes a small, pre-computed SUMMARY of "what sub-slice of
// the live document/shell actually changed in this commit" — booleans the store
// derives at the chokepoint from the immutable-update diff (entity mutations,
// styleTable/header changes) plus the pre/post context + selection values. It
// owns NO contracts/store types (so it stays a leaf in `drawing-model`), and it
// is the single source of truth for "a mutation that changed entity state bumps
// geometry; one that changed a table/header bumps table; one that changed the
// active space/layout-context bumps context; one that changed the selection set
// bumps selection — and a commit that changed NONE of these bumps nothing".

/**
 * The pre-computed change summary for ONE committed mutation, derived at the
 * store's universal commit chokepoint from the immutable-update diff. Every
 * field is a VALUE/membership comparison (not a reference compare) so a
 * dev-mode deep-clone of the document — or a reducer that re-spreads an
 * unchanged `selection` object — cannot register a phantom change.
 */
export interface RevisionStateDiff {
  /**
   * Entity STATE changed — an entity was added / removed / updated in the
   * geometry document (the `EntityMutation` added/removed/updated slices are
   * non-empty). → GEOMETRY.
   */
  readonly entityStateChanged: boolean;
  /**
   * A named-object / header TABLE changed — a style table was touched, OR a
   * header/layout-table/block-definition field differs between the pre- and
   * post-mutation document (the store's `docHeaderChanged` predicate). → TABLE.
   */
  readonly tableChanged: boolean;
  /**
   * The editing CONTEXT changed — the active space id and/or the layout context
   * ("model" / "paper") differs pre→post. Covers `shell/activate-space`,
   * `shell/set-layout-context`, BEDIT/REFEDIT enter/exit, AND the implicit
   * active-space change when the ACTIVE layout is removed. → CONTEXT.
   */
  readonly contextChanged: boolean;
  /**
   * The SELECTION set membership changed — the post selection is not the same
   * SET of ids as the pre selection (order-insensitive, AutoCAD pickset
   * semantics). Re-selecting the identical set, or clearing an already-empty
   * selection, is NOT a change. → SELECTION.
   */
  readonly selectionChanged: boolean;
}

/**
 * Order-insensitive SET-membership equality for two id lists (AutoCAD pickset
 * semantics — a pickset is a SET, so a re-order is not a membership change).
 * Used by {@link selectionMembershipChanged}; exported for the store + tests.
 *
 * G4 (@codex #6) — DUPLICATE-safe. The selection schema does NOT enforce id
 * uniqueness and the shell stores the raw array, so a list can carry dupes. The
 * comparison must be over the UNIQUE id SETs (not a length+subset multiset test,
 * which both mis-reported `[a,a,b]` vs `[a,b]` as DIFFERENT — a phantom bump on a
 * dup-collapse no-op — and `[a,a,b]` vs `[a,b,b]` as SAME — a missed real change):
 *   - build each side's unique set; equal IFF same size AND every member of one
 *     is in the other. `[a,a,b]` ⇔ `[a,b]` (same set, no bump); `[a,b]` vs
 *     `[a,b,c]` differ (bump); `[a,a,b]` vs `[a,b,b]` are the SAME set `{a,b}`
 *     (no membership change — only the multiplicity differs, which a pickset
 *     ignores).
 */
export function sameIdSet(
  a: readonly string[],
  b: readonly string[],
): boolean {
  if (a === b) return true;
  const setA = new Set(a);
  const setB = new Set(b);
  if (setA.size !== setB.size) return false;
  for (const id of setA) {
    if (!setB.has(id)) return false;
  }
  return true;
}

/** Whether the selection SET membership changed pre→post (no-op suppression). */
export function selectionMembershipChanged(
  prev: readonly string[],
  next: readonly string[],
): boolean {
  return !sameIdSet(prev, next);
}

/**
 * The bump-class SET a committed mutation touched, derived from the live
 * {@link RevisionStateDiff} rather than the command kind. This is the
 * convergent classifier the store uses at the universal commit chokepoint and
 * at the undo/redo inverse application: it bumps EXACTLY the classes whose
 * sub-slice actually changed, so a missed path cannot under-bump, a runtime-
 * dependent effect cannot mis-class, and a no-op commit bumps nothing.
 */
export function diffBumpClasses(diff: RevisionStateDiff): BumpClassSet {
  if (
    !diff.entityStateChanged &&
    !diff.tableChanged &&
    !diff.contextChanged &&
    !diff.selectionChanged
  ) {
    return EMPTY_BUMP_CLASSES;
  }
  return Object.freeze({
    geometry: diff.entityStateChanged,
    table: diff.tableChanged,
    context: diff.contextChanged,
    selection: diff.selectionChanged,
  });
}

/** The fresh-document counter baseline (epoch 1, every revision 0). */
export function initialRevisionCounters(): RevisionCounters {
  return {
    snapshotRevision: 0,
    geometryRevision: 0,
    tableRevision: 0,
    contextRevision: 0,
    selectionRevision: 0,
    documentEpoch: 1,
  };
}

/**
 * DUAL-BUMP: bump `snapshotRevision` ONCE plus each finer sibling whose class is
 * present. An EMPTY set is a no-op (returns the SAME reference — empty/aborted
 * commits must not advance any counter, INV-9; the identity equality lets a
 * caller cheaply detect "nothing changed").
 */
export function bumpForClasses(
  counters: RevisionCounters,
  classes: BumpClassSet,
): RevisionCounters {
  if (!hasAnyBumpClass(classes)) return counters;
  return {
    snapshotRevision: counters.snapshotRevision + 1,
    geometryRevision:
      counters.geometryRevision + (classes.geometry ? 1 : 0),
    tableRevision: counters.tableRevision + (classes.table ? 1 : 0),
    contextRevision: counters.contextRevision + (classes.context ? 1 : 0),
    selectionRevision:
      counters.selectionRevision + (classes.selection ? 1 : 0),
    documentEpoch: counters.documentEpoch,
  };
}

/**
 * Reset all five revisions to 0 and MINT a fresh `documentEpoch` (monotonic
 * increment). Called only on a true document SWAP (OPEN a different file in the
 * same tab) — NOT on an INSERT/import re-stamp of the same document id. The epoch
 * is the agent's "this is a different document now" signal; it must never repeat,
 * so it increments from the prior value rather than resetting to 1.
 */
export function mintNewDocumentEpoch(
  counters: RevisionCounters,
): RevisionCounters {
  return {
    snapshotRevision: 0,
    geometryRevision: 0,
    tableRevision: 0,
    contextRevision: 0,
    selectionRevision: 0,
    documentEpoch: counters.documentEpoch + 1,
  };
}

// ── Bump-class classifier (command-type → finer bump classes) ───────────────
//
// Mirrors the domain-parse of `contracts/auditTaxonomy.ts categoryForCommandType`
// but maps to {@link BumpClass} and SPLITS the two `AuditCategory` families that
// straddle the bump-class boundary:
//   - `layout`: `shell/activate-space` + `shell/set-layout-context` are the
//     active-space SWITCH → CONTEXT; `shell/add-space`/`remove-space`/
//     `rename-space` are LAYOUT-TABLE edits → TABLE.
//   - `space`: `space/enter-*` / `space/exit-*` are pure editing-context SWITCHES
//     → CONTEXT; `space/save-block-edit` + `space/refedit-*-working-set` write a
//     block/xref DEFINITION → TABLE.
// Every other family maps 1:1 (see the per-family comments). Unrecognised → none.

/** CONTEXT subset of the `shell/*` layout family (active-space switch). */
const SHELL_CONTEXT_RESTS = new Set<string>([
  "activate-space",
  "set-layout-context",
]);

/** TABLE subset of the `shell/*` layout family (layout-table edits). */
const SHELL_LAYOUT_TABLE_RESTS = new Set<string>([
  "add-space",
  "remove-space",
  "rename-space",
]);

/** SELECTION subset of the `shell/*` family. */
const SHELL_SELECTION_RESTS = new Set<string>([
  "select-entities",
  "clear-selection",
]);

/** GEOMETRY (lifecycle) subset of the `shell/*` family. */
const SHELL_LIFECYCLE_RESTS = new Set<string>([
  "delete-entities",
  "restore-entities",
]);

/** TABLE subset of the `space/*` family (writes a block/xref definition). */
const SPACE_TABLE_RESTS = new Set<string>([
  "save-block-edit",
  "refedit-add-to-working-set",
  "refedit-remove-from-working-set",
]);

/**
 * Per-INSTANCE block commands that edit how a specific INSERT renders — an
 * entity-STATE change (GEOMETRY), NOT a block-DEFINITION (table) edit. All
 * contain `insert`, so they are excluded from the block-DEFINITION-authoring
 * keyword test below and routed to GEOMETRY.
 */
const GEOMETRY_INSERT_INSTANCE_RESTS = new Set<string>([
  "explode-insert",
  "set-insert-parameter-override",
  "clear-insert-parameter-override",
  "set-insert-visibility-state",
]);

/**
 * Classify the `geometry/<rest>` sub-domain. Block-DEFINITION + dynamic-block
 * authoring → TABLE (the block table); everything else (create / modify /
 * lifecycle / multileader / attribute / constraint / annotation / viewport
 * per-VP override) is an entity-STATE change → GEOMETRY.
 */
/**
 * `geometry/create-*` rests that create a block-DEFINITION-authoring STRUCTURE
 * (block def, lookup table, chain group) rather than a drawable ENTITY. These
 * are TABLE edits and MUST be tested before the generic entity `create-` arm.
 */
const BLOCK_TABLE_CREATE_RESTS = new Set<string>([
  "create-block-definition",
  "create-lookup-table",
  "create-chain-group",
]);

function classifyGeometryRest(rest: string): BumpClass {
  // `restore-viewport-clip` (system-only delete inverse) is a viewport entity op.
  if (rest === "restore-viewport-clip") return "geometry";
  // @codex PR #785 round 2, F2 — the `properties/apply` PRESENCE-BIT restore
  // replaces an EXISTING entity with its full pre-edit snapshot: an entity-STATE
  // change, so GEOMETRY (it touches no BLOCK/style/layout table). Stated
  // explicitly rather than left to the generic tail below: the fallthrough
  // happens to return "geometry" today only because the literal contains none
  // of the `isDynamicBlockAuthoringRest` keywords ("block"/"visibility"/
  // "lookup"/"chain"/"dynamic"/"parameter"/"action"/"property") and does not
  // match the `create-`/`viewport-` prefixes or the exact `delete-entity` /
  // `restore-entity` arms — an accident of spelling, not a classification.
  if (rest === "restore-entity-snapshot") return "geometry";
  if (rest.startsWith("viewport-")) return "geometry";
  // Block-DEFINITION authoring create + remove touch the BLOCK table, not an
  // entity's own geometry state — tested BEFORE the generic `create-` /
  // entity-creation arms below (else they slip to geometry via the prefix).
  if (BLOCK_TABLE_CREATE_RESTS.has(rest) || rest === "remove-block-definition")
    return "table";
  // `geometry/create-block-insert` + every other `create-*` is a true ENTITY
  // creation (places geometry / a reference), so it is GEOMETRY.
  if (rest.startsWith("create-")) return "geometry";
  if (rest === "delete-entity" || rest === "restore-entity") return "geometry";
  // Per-INSTANCE insert overrides + explode are entity-STATE edits (geometry),
  // tested before the block-DEFINITION-authoring keyword check.
  if (GEOMETRY_INSERT_INSTANCE_RESTS.has(rest)) return "geometry";
  // Dynamic-block AUTHORING (params/actions/visibility/block-property) edits the
  // BLOCK-definition table (shared one-truth predicate; `insert` already routed
  // to geometry above).
  if (isDynamicBlockAuthoringRest(rest)) return "table";
  // Everything else is an entity-STATE edit (move/rotate/scale/.../attribute/
  // constraint/annotation/multileader).
  return "geometry";
}

/**
 * Map a command-type literal to its single finer {@link BumpClass}, or `null` if
 * the command is NOT a document mutation (session / ui / commandline / history /
 * system / dynamic-CLI / view-op / interaction). The caller bumps nothing for a
 * `null` result. Total over the real command unions — see `revisionModel.test.ts`.
 */
export function bumpClassForCommandType(commandType: string): BumpClass | null {
  const slash = commandType.indexOf("/");
  const domain = slash === -1 ? commandType : commandType.slice(0, slash);
  const rest = slash === -1 ? "" : commandType.slice(slash + 1);

  switch (domain) {
    // Entity property / metadata edits (CHPROP / MATCHPROP / Properties).
    case "properties":
    case "chprop":
    case "matchprop":
      return "geometry";

    // Selection-set membership.
    case "selection":
      return "selection";

    // BEDIT / REFEDIT / TESTBLOCK: enter/exit are context switches; the
    // definition-writing arms are table edits.
    case "space":
      return SPACE_TABLE_RESTS.has(rest) ? "table" : "context";

    // Header tables: units / sysvar header, layer-delete, style-table edits,
    // annotation-scale list edits — all document structure (TABLE).
    case "header":
      return "table";

    // Block-table file ops: purge / rename-block / xref attach-bind-unload-
    // reload-detach-clip.
    case "file":
      return "table";

    // Annotative context bound to an ENTITY.
    case "entity":
      return "geometry";

    case "shell":
      if (SHELL_CONTEXT_RESTS.has(rest)) return "context";
      if (SHELL_LAYOUT_TABLE_RESTS.has(rest)) return "table";
      if (SHELL_SELECTION_RESTS.has(rest)) return "selection";
      if (SHELL_LIFECYCLE_RESTS.has(rest)) return "geometry";
      // shell/open-panel — a UI panel switch, not a document mutation.
      return null;

    case "geometry":
      return classifyGeometryRest(rest);

    // NON-mutation families: session switch, command-line feedback, history
    // markers (the inner re-dispatched command bumps via its own real type),
    // dialog-open shell events, DYN* CLI storage-only reads.
    case "session":
    case "commandline":
    case "history":
    case "system":
    case "dynamic":
      return null;

    default:
      return null;
  }
}

/**
 * Commands whose PRIMARY class is NOT `selection` but which ALSO reset / filter
 * the selection set as a SIDE EFFECT — so they must DUAL-BUMP `selectionRevision`
 * too (an agent gating on `baseSelectionRevision` would otherwise miss the wipe).
 *   - `shell/activate-space`  — switching space drops the whole selection.
 *   - `shell/remove-space`    — removes the space's entities from the selection.
 *   - `shell/delete-entities` / `geometry/delete-entity` — erased entities leave
 *     the selection set.
 * (`shell/clear-selection` / `shell/select-entities` are PRIMARY-selection and
 * already covered.)
 */
const ALSO_TOUCHES_SELECTION = new Set<string>([
  "shell/activate-space",
  "shell/remove-space",
  "shell/delete-entities",
  "geometry/delete-entity",
]);

/**
 * The bump-class SET a single committed command touches. The PRIMARY class comes
 * from {@link bumpClassForCommandType}; commands in {@link ALSO_TOUCHES_SELECTION}
 * additionally union the `selection` class (a multi-class side effect, INV-9
 * DUAL-BUMP). `null` primary with no side effect → the empty set (no bump). This
 * set form is what {@link bumpForClasses} and the batch accumulator consume.
 */
export function bumpClassesForCommandType(commandType: string): BumpClassSet {
  const primary = bumpClassForCommandType(commandType);
  const alsoSelection = ALSO_TOUCHES_SELECTION.has(commandType);
  if (primary === null) {
    return alsoSelection ? singleBumpClass("selection") : EMPTY_BUMP_CLASSES;
  }
  return alsoSelection && primary !== "selection"
    ? unionBumpClasses(singleBumpClass(primary), singleBumpClass("selection"))
    : singleBumpClass(primary);
}
